/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useState, useRef, useCallback } from 'react'
import AchievementToast from '../components/AchievementToast'

const AchievementContext = createContext(null)

const MAX_QUEUE_LEN = 5
const DEDUP_WINDOW_MS = 3000

export function AchievementProvider({ children }) {
    const [toast, setToast] = useState(null)
    const queueRef = useRef([])
    // Recently-shown signature → timestamp. Same (type|message) within 3s is dropped.
    const recentRef = useRef(new Map())

    const showNext = useCallback(() => {
        if (queueRef.current.length > 0) {
            setToast(queueRef.current.shift())
        } else {
            setToast(null)
        }
    }, [])

    const showAchievement = useCallback((opts) => {
        if (!opts) return
        const sig = `${opts.type || ''}|${opts.message || ''}`
        const now = Date.now()
        const lastShown = recentRef.current.get(sig)
        if (lastShown && now - lastShown < DEDUP_WINDOW_MS) {
            // Same toast just shown — drop silently
            return
        }
        recentRef.current.set(sig, now)
        // Garbage-collect entries older than 30s
        for (const [k, t] of recentRef.current) {
            if (now - t > 30_000) recentRef.current.delete(k)
        }

        setToast((current) => {
            if (!current) return opts
            // Error toasts jump to front of queue
            if (opts.type === 'error') {
                queueRef.current.unshift(opts)
            } else if (queueRef.current.length < MAX_QUEUE_LEN) {
                queueRef.current.push(opts)
            }
            // Beyond MAX_QUEUE_LEN we silently drop — when 50 cards complete in
            // a row the user only sees the first 5 toasts, not all 50.
            return current
        })
    }, [])

    const dismissToast = useCallback(() => {
        showNext()
    }, [showNext])

    return (
        <AchievementContext.Provider value={{ showAchievement }}>
            {children}
            {toast && (
                <AchievementToast
                    type={toast.type}
                    title={toast.title}
                    message={toast.message}
                    onClick={toast.onClick}
                    duration={toast.duration}
                    onDismiss={dismissToast}
                />
            )}
        </AchievementContext.Provider>
    )
}

export function useAchievement() {
    const ctx = useContext(AchievementContext)
    return ctx || { showAchievement: () => {} }
}
